import { buildInstance, instance } from '@/api/instances'
import { addAutofillLog } from '@/components/clinicalRecord/utils/log'
import {
  CLINICAL_RECORD_STATUS,
  DISCARD_TYPES,
  EVENT_TYPE,
  LOG_ACTIONS,
  LOG_ACTIONS_AUTOFILL,
  LOG_ORIGIN,
  TIME_TO_UPDATE
} from '@/models/clinicalRecord/base'
import {
  TEMPLATE_OPTIONS,
  TEMPLATE_TYPES
} from '@/models/clinicalRecord/template'
import useCertificateLog from '@/modules/user/hooks/useCertificateLog'
import Logger, {
  EventModel,
  EventName,
  LogLevel,
  ProviderType
} from '@/wrappers/logger'

const create = async (
  store,
  { templateType, event, personId, queryParams }
) => {
  if (!event && queryParams?.channelId) {
    event = {
      name: '',
      referenceModel: EVENT_TYPE.CHANNEL,
      referenceModelId: queryParams.channelId
    }
  }
  try {
    const components = TEMPLATE_OPTIONS.find(
      template => template.value === templateType
    )?.components
    const body = {
      personId,
      components,
      description: '',
      excuseNotes: [],
      templateType,
      event
    }

    const clinicalRecord = await instance.post('appointments', {
      json: body
    })

    return clinicalRecord
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/clinicalRecord/create' },
      { root: true }
    )

    return null
  }
}

const get = async (store, { clinicalRecordId, signal }) => {
  try {
    return await instance.get(
      `appointments/${clinicalRecordId}?load_tasks=true`,
      { signal }
    )
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/clinicalRecord/get' },
      { root: true }
    )
  }
}

const update = async (store, { clinicalRecord, shouldShowError }) => {
  const timeStartUpdate = new Date().getTime()
  try {
    Logger.addLog(EventName.USER_INTERACTION_HAI, {
      model: EventModel.APPOINTMENT,
      modelId: clinicalRecord.id,
      data: {
        time: TIME_TO_UPDATE // time in millis. This value is based on debounce time to update appointment.
      }
    })
    const clinicalRecordUpdated = await instance.put(
      `appointments/${clinicalRecord.id}`,
      {
        json: {
          ...clinicalRecord,
          completed: false,
          status: CLINICAL_RECORD_STATUS.DRAFT
        }
      }
    )

    Logger.addLog(
      EventName.USER_INTERACTION_FORM,
      {
        model: EventModel.APPOINTMENT,
        modelId: clinicalRecord.id,
        data: {
          context: 'update',
          value: clinicalRecordUpdated.version,
          time: timeStartUpdate
        }
      },
      {
        isAction: true,
        logLevel: LogLevel.INFO
      },
      [ProviderType.DATADOG]
    )
    return {
      version: clinicalRecordUpdated.version,
      updatedAt: clinicalRecordUpdated.updatedAt
    }
  } catch (error) {
    if (shouldShowError) {
      store.dispatch(
        'errors/setError',
        { error, request: 'pillars/clinicalRecord/get' },
        { root: true }
      )
    }
    Logger.addLog(
      EventName.INTERACTION_ERROR,
      {
        model: EventModel.APPOINTMENT,
        modelId: clinicalRecord.id,
        data: {
          error: {
            errorMessage: error?.message,
            errorResponseCode: error.response?.data
              ? error.response.data.code
              : null
          },
          value: clinicalRecord.version,
          time: timeStartUpdate
        }
      },
      {
        isAction: true,
        logLevel: LogLevel.INFO
      },
      [ProviderType.DATADOG]
    )
    return null
  }
}

const remove = async (store, { reason, clinicalRecord }) => {
  try {
    if (reason.type === DISCARD_TYPES.NO_SHOW) {
      const result = await store.dispatch('update', {
        clinicalRecord,
        shouldShowError: true
      }) //get last updates before discard
      if (!result) return
    }
    return await instance.delete(`appointments/${clinicalRecord.id}`, {
      json: {
        type: reason.type,
        reason: reason.reason
      }
    })
  } catch {
    store.commit(
      'errors/setError',
      'Não foi possível descartar esse rascunho',
      { root: true }
    )
  }
}

const publish = async (store, clinicalRecordId) => {
  try {
    const { addLogOnPublishTask } = useCertificateLog()
    addLogOnPublishTask(
      store.rootState.pillars.personId,
      EventModel.APPOINTMENT,
      null,
      clinicalRecordId,
      store.rootState.user?.certification?.token
    )

    await instance.post(`appointments/${clinicalRecordId}/finish`, null, {
      headers: {
        'bird-id-token': store.rootState.user?.certification?.token
      }
    })
    return { success: true }
  } catch (error) {
    const errorResponseCode = error.response?.data
      ? error.response.data.code
      : null
    const handledError = handleErrorPublish(errorResponseCode)
    if (handledError) return handledError

    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/clinicalRecord/publish' },
      { root: true }
    )
    return { success: false }
  }
}

const handleErrorPublish = errorResponseCode => {
  if (errorResponseCode) {
    if (errorResponseCode === 'invalid_appointment_event') {
      return {
        success: false,
        errors: getErrorMessage('event')
      }
    } else if (
      errorResponseCode === 'invalid_appointment_case_record_details'
    ) {
      return {
        success: false,
        errors: getErrorMessage('caseRecordDetails')
      }
    } else if (
      errorResponseCode ===
      'excuse_notes_without_appointment_case_record_details'
    ) {
      return {
        success: false,
        errors: getErrorMessage('excuseNotesWithoutDemand')
      }
    }
  }
  return null
}

const getErrorMessage = field => {
  const errors = {
    event: [
      {
        message: 'Você precisa vincular um evento a esse atendimento.',
        field: 'event'
      },
      {
        field: 'emptyEventReason',
        message: 'É necessário adicionar uma justificativa para o vínculo vazio'
      }
    ],
    caseRecordDetails: [
      {
        field: 'caseRecordDetails',
        message: 'É obrigatório cadastrar uma demanda.'
      }
    ],
    excuseNotesWithoutDemand: [
      {
        field: 'caseRecordDetails',
        message: 'É necessário cadastrar uma demanda ao inserir atestado médico'
      }
    ]
  }
  return errors[field]
}

const searchScript = async (store, { query, signal }) => {
  try {
    const result = await instance.get(`bud/protocols?name=${query}`, {
      ...signal
    })

    return result
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/clinicalRecord/searchScript' },
      { root: true }
    )
    return []
  }
}

const searchMacro = async (store, { query, componentType, signal }) => {
  try {
    return await instance.get(
      `appointment_macros?type=${componentType}&title=${query}`,
      {
        signal
      }
    )
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/clinicalRecord/searchMacro' },
      { root: true }
    )
    return []
  }
}

const searchEvent = async (store, { type }) => {
  const personId = store.rootState.pillars.personId

  try {
    return await instance.get(
      `persons/${personId}/appointment_events?referenceModel=${type}`
    )
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'pillars/clinicalRecord/searchEvent',
        message: 'Erro ao buscar eventos.'
      },
      { root: true }
    )
    return []
  }
}

const getSuggestionEvents = async store => {
  const personId = store.rootState.pillars.personId
  try {
    return await instance.get(
      `persons/${personId}/appointment_events/suggestion`
    )
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'pillars/clinicalRecord/getSuggestionEvents',
        message: 'Erro ao obter sugestões de eventos.'
      },
      { root: true }
    )
    return []
  }
}

const updateOpenRelatedPillars = async store => {
  const personId = store.rootState.pillars.personId

  store.dispatch(
    'pillars/profile/fetchLiteProfile',
    { personId },
    { root: true }
  )
}

const createWithEvent = async (
  store,
  { event, personId, templateType = TEMPLATE_TYPES.SOAP }
) => {
  const clinicalRecord = await store.dispatch('create', {
    templateType,
    event,
    personId
  })
  if (clinicalRecord) {
    Logger.addLog(EventName.USER_INTERACTION_CREATE, {
      model: EventModel.APPOINTMENT,
      modelId: clinicalRecord.id,
      data: {
        action: LOG_ACTIONS.ORIGIN,
        value: event,
        context:
          event.referenceModel === EVENT_TYPE.CHANNEL
            ? LOG_ORIGIN.CHANNEL
            : LOG_ORIGIN.SCHEDULING
      }
    })
    return clinicalRecord?.id
  }
}

const getExcuseNoteById = async (store, excuseNoteId) => {
  try {
    return await instance.get(`appointments/excuse_notes/${excuseNoteId}`)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/clinicalRecord/getExcuseNoteById' },
      { root: true }
    )
  }
}

const createIAContent = async (
  store,
  { clinicalRecordId, channelId, componentType, signal }
) => {
  try {
    const p1 = performance.now()

    const instanceIA = buildInstance({ timeout: 60000 })

    const response = await instanceIA.get(
      `appointments/${clinicalRecordId}/autofill`,
      {
        params: {
          channel_id: channelId,
          components: componentType ? componentType : undefined
        },
        signal
      }
    )

    const p2 = performance.now()
    addAutofillLog(LOG_ACTIONS_AUTOFILL.SUCCESS, {
      duration: p2 - p1,
      clinicalRecordId
    })

    return response
  } catch (error) {
    addAutofillLog(LOG_ACTIONS_AUTOFILL.FAIL, {
      clinicalRecordId
    })
    throw error
  }
}

const reviewIAContent = (_, { clinicalRecordId, componentType }) => {
  instance.post(`appointments/${clinicalRecordId}/autofill/review`, {
    json: {
      component: componentType,
      action: 'ACKNOWLEDGE'
    }
  })
}

const generateIAContent = async (
  store,
  { clinicalRecordId, channelId, signal, componentType }
) => {
  try {
    const response = await store.dispatch('createIAContent', {
      clinicalRecordId,
      channelId,
      componentType,
      signal
    })

    if (!response) {
      return [true, null] //Empty response
    }

    return [false, response.soap]
  } catch (error) {
    if (error.name === 'CanceledError' || error.name === 'AbortError') {
      return [false, null] //Ignore abort error
    }

    return [true, error]
  }
}

const getExternalFiles = async (store, clinicalRecord) => {
  try {
    const clinicalRecordEvent = clinicalRecord.event
    const response = await instance.get(
      `appointments/${clinicalRecord.id}/external_files/${clinicalRecordEvent.referenceModelId}`
    )

    return response.externalFiles
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'pillars/clinicalRecord/getExternalFiles',
        message: 'Erro ao recuperar as imagens do canal.'
      },
      { root: true }
    )
    return []
  }
}

export default {
  create,
  get,
  remove,
  update,
  searchScript,
  searchMacro,
  searchEvent,
  getSuggestionEvents,
  updateOpenRelatedPillars,
  publish,
  createWithEvent,
  getExcuseNoteById,
  generateIAContent,
  createIAContent,
  reviewIAContent,
  getExternalFiles
}
